import React from 'react';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <section className="space-y-3">
        <h2 className="text-xl font-semibold text-snapfy-text-primary">{title}</h2>
        <div className="text-snapfy-text-secondary space-y-2">{children}</div>
    </section>
);

const SectionDivider: React.FC = () => (
    <div className="py-4">
        <div className="w-16 border-t border-snapfy-border"></div>
    </div>
);

const TermsPage: React.FC = () => {
    return (
        <div className="max-w-3xl mx-auto py-8 px-4 text-snapfy-text-secondary">
            <header className="mb-8">
                <h1 className="text-3xl sm:text-4xl font-bold text-snapfy-text-primary mb-2">Termos & Condições</h1>
                <p className="text-sm text-snapfy-text-secondary">Última atualização: 17 de novembro de 2025.</p>
            </header>

            <div className="space-y-8">
                <p className="text-snapfy-text-secondary">
                    Estes Termos e Condições regulam o uso da plataforma Snapfy.ai, oferecida por SNAPFY.AI LTDA, inscrita no CNPJ sob o nº 57.189.141/0001-11, doravante denominada simplesmente “Snapfy.ai”.
                </p>
                <p className="text-snapfy-text-secondary">
                    Ao acessar ou utilizar a plataforma, o usuário declara ter lido, compreendido e aceitado integralmente estes termos.
                </p>

                <SectionDivider />

                <Section title="1. Objeto">
                    <p>
                        A Snapfy.ai é uma plataforma online que permite a geração de imagens realistas com modelos virtuais a partir de fotos still ou desenhos técnicos de produtos, utilizando tecnologia de inteligência artificial.
                    </p>
                </Section>

                <SectionDivider />

                <Section title="2. Acesso à plataforma">
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>O acesso à plataforma se dá mediante criação de conta e adesão a um plano mensal com liberação de créditos mensais.</li>
                        <li>É necessário fornecer dados válidos e manter as informações atualizadas.</li>
                        <li>O acesso é pessoal, intransferível e protegido por senha.</li>
                    </ul>
                </Section>

                <SectionDivider />

                <Section title="3. Plano gratuito (trial)">
                     <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>Ao criar uma conta, o usuário recebe 10 créditos gratuitos (equivalente a 10 imagens) para testar a plataforma.</li>
                        <li>O período de teste tem duração de 7 dias corridos, com a obrigatoriedade de cadastro de cartão de crédito.</li>
                        <li>A cobrança do plano pago é iniciada automaticamente após o término do período de teste, caso o usuário não cancele dentro desse prazo.</li>
                    </ul>
                </Section>

                <SectionDivider />

                <Section title="4. Créditos e uso">
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>Cada imagem gerada consome 1 crédito.</li>
                        <li>Os créditos são renovados mensalmente e acumulam caso não sejam utilizados.</li>
                        <li>Os créditos não expiram enquanto o plano estiver ativo.</li>
                        <li>Em caso de cancelamento, os créditos permanecem vinculados à conta, podendo ser utilizados após reativação do plano.</li>
                    </ul>
                </Section>

                <SectionDivider />

                 <Section title="5. Cancelamento e renovação">
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>O plano é renovado automaticamente a cada ciclo (mensal).</li>
                        <li>O usuário pode cancelar a assinatura a qualquer momento, mantendo o acesso até o final do ciclo vigente.</li>
                        <li>Após o término do ciclo, o acesso à plataforma será suspenso até a reativação.</li>
                    </ul>
                </Section>

                <SectionDivider />

                <Section title="6. Propriedade das imagens">
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>As imagens geradas pelo usuário podem ser utilizadas livremente para fins comerciais, promocionais ou editoriais.</li>
                        <li>A Snapfy.ai não reivindica direitos autorais sobre o conteúdo gerado, mas reserva-se o direito de utilizar imagens de forma anonimizada para fins de demonstração, portfólio e marketing, salvo solicitação contrária do cliente.</li>
                    </ul>
                </Section>

                <SectionDivider />

                <Section title="7. Restrições de uso">
                    <p>É proibido:</p>
                     <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>Utilizar a plataforma para fins ilegais ou não autorizados.</li>
                        <li>Enviar conteúdos ofensivos, discriminatórios ou que violem direitos de terceiros.</li>
                        <li>Tentar acessar áreas ou dados não autorizados.</li>
                        <li>Compartilhar acessos ou burlar o sistema de créditos.</li>
                    </ul>
                </Section>
                
                <SectionDivider />

                <Section title="8. Suporte">
                    <p>
                        O suporte técnico é oferecido via e-mail ou WhatsApp, de segunda a sexta, das 9h às 18h (horário de Brasília).
                    </p>
                </Section>

                <SectionDivider />

                <Section title="9. Privacidade">
                    <p>
                        A Snapfy.ai respeita a privacidade dos seus usuários e trata os dados pessoais de acordo com a Lei Geral de Proteção de Dados – LGPD (Lei nº 13.709/18).
                    </p>
                    <p>
                        Para mais detalhes, consulte nossa <a href="#" className="text-snapfy-accent-soft hover:underline">Política de Privacidade</a>.
                    </p>
                </Section>
                
                <SectionDivider />

                <Section title="10. Alterações nos termos">
                    <p>
                        A Snapfy.ai poderá atualizar estes termos a qualquer momento. As alterações serão notificadas via e-mail ou através da própria plataforma. O uso contínuo da plataforma após as alterações representa concordância com os novos termos.
                    </p>
                </Section>
                
                <SectionDivider />

                <Section title="11. Foro">
                    <p>
                        Fica eleito o foro da comarca de São Paulo/SP para dirimir eventuais conflitos decorrentes deste contrato, com renúncia a qualquer outro, por mais privilegiado que seja.
                    </p>
                </Section>
            </div>
        </div>
    );
};

export default TermsPage;